import { Controller } from '@hotwired/stimulus';

/**
 * Tag Remove Controller
 *
 * Wires the ✕ of a removable CR « remarque » tag chip (Ui::ChipComponent with
 * `removable: true` + a `remove_url`). Mounted directly on the chip's remove
 * BUTTON (the component hoists `data-controller="tag-remove"` +
 * `data-tag-remove-url-value` + `data-action="click->tag-remove#remove"` onto the
 * button when a `remove_url` is present).
 *
 * On click it DELETEs the tagging and hands the `text/vnd.turbo-stream.html`
 * response to `Turbo.renderStreamMessage` — the server replaces the stable
 * `remark-tags-<observation_id>` block (rendered identically in the edit form's
 * Tags disclosure AND the read fiche), so removal works on BOTH surfaces with no
 * surrounding `<form>` required. Mirrors the fetch/CSRF/Turbo-Stream mechanics of
 * `cr-inline-field`.
 *
 * Values:
 *   - url : the DELETE endpoint (the Tagging member route).
 *
 * Actions:
 *   - remove : `click` on the chip ✕ — DELETE + render the returned Turbo Stream.
 *
 * @example
 *   <button type="button" data-controller="tag-remove"
 *           data-tag-remove-url-value="/…/remarques/47/tags/9"
 *           data-action="click->tag-remove#remove" aria-label="Retirer le tag Sécurité">…</button>
 */
export default class extends Controller {
  static values = { url: String };

  /**
   * DELETE the tagging, then render the returned Turbo Stream (which replaces the
   * `remark-tags-<id>` block). Guarded on the Turbo-Stream content-type so an
   * auth-expired 302→login HTML page never gets fed to `renderStreamMessage`.
   *
   * @param {Event} event
   */
  async remove(event) {
    event.preventDefault();
    if (!this.urlValue || this.busy) return;

    this.busy = true;
    this.element.disabled = true;

    try {
      const response = await fetch(this.urlValue, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': this.#csrfToken(),
        },
      });

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('text/vnd.turbo-stream.html')) {
        window.Turbo?.renderStreamMessage?.(await response.text());
      } else {
        this.element.disabled = false;
        console.error(`[tag-remove] DELETE failed: HTTP ${response.status} (${contentType})`);
      }
    } catch (error) {
      this.element.disabled = false;
      console.error('[tag-remove] DELETE failed:', error);
    } finally {
      this.busy = false;
    }
  }

  /**
   * CSRF token from the standard Rails `<meta name="csrf-token">` tag.
   *
   * @returns {string}
   */
  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
  }
}
